import { AlertTriangle, ChevronLeft, ChevronRight, CloudCheck, CloudSync, Lock, Plus, Trash2, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next'; // <-- NEU
import { SPLIT_THRESHOLDS } from '../../../../../lib/constants.js';
import type { SubmissionReviewStore } from '../hooks/use-submission-state.js';
import { UNASSIGNED_BUCKET_ID } from '../utils.js';
import { PageThumbnail } from './page-thumbnail.js';

export function ReviewTopBar({ store }: { store: SubmissionReviewStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { state, actions } = store;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 pb-3 bg-white dark:bg-card border-b border-slate-200 dark:border-slate-700 shrink-0">
      <div className="flex gap-3">
        {!state.isReadOnly && (
          <button
            onClick={actions.addNewStudent}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> {t('Canvas.submissionCheck.newStudent')}
          </button>
        )}
      </div>

      <div className="flex items-center gap-4">
        {state.error && <span className="text-sm font-medium text-red-600">{state.error}</span>}
        {state.isReadOnly ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs shadow-sm select-none">
            <span className="font-semibold">{t('Canvas.submissionCheck.readOnly')}</span>
          </div>
        ) : (
          <button
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-50 transition whitespace-nowrap"
          >
            {state.isSaving ? <CloudSync className="w-4 h-4 animate-spin" /> : <CloudCheck className="w-4 h-4" />}
            {t('Canvas.submissionCheck.saveAssignment')}
          </button>
        )}
      </div>
    </div>
  );
}

export function UnassignedPagesBucket({ store }: { store: SubmissionReviewStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { state, actions, lightbox } = store;
  if (state.unassignedPages.length === 0) return null;

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-4 transition-colors ${
        state.dropTarget?.bucketId === UNASSIGNED_BUCKET_ID
          ? 'bg-amber-100 border-amber-400'
          : 'bg-amber-50/50 border-amber-300'
      }`}
      onDragOver={state.isReadOnly ? undefined : e => actions.handleDragOver(e, UNASSIGNED_BUCKET_ID, -1)}
      onDrop={state.isReadOnly ? undefined : e => actions.handleDrop(e, UNASSIGNED_BUCKET_ID, -1)}
    >
      <div className="flex items-center gap-2 text-amber-800 font-semibold mb-3">
        <AlertTriangle className="w-5 h-5" />
        {t('Canvas.submissionCheck.unassignedPagesTitle', { count: state.unassignedPages.length })}
        <span className="text-xs font-normal text-amber-600 ml-2">
          {t('Canvas.submissionCheck.unassignedPagesHint')}
        </span>
      </div>
      <div className="flex overflow-x-auto gap-3 pb-2 custom-scrollbar">
        {state.unassignedPages.map((page, idx) => (
          <PageThumbnail
            key={`unassigned-${page.imageId}`}
            page={page}
            idx={idx}
            bucketId={UNASSIGNED_BUCKET_ID}
            isReadOnly={state.isReadOnly}
            fallbackUrl={state.fallbackUrls[page.imageId]}
            draggedItem={state.draggedItem}
            dropTarget={state.dropTarget}
            onDragStart={actions.handleDragStart}
            onDragOver={actions.handleDragOver}
            onDrop={actions.handleDrop}
            onPreview={(bucketId, index) => lightbox.actions.setPreviewPage({ bucketId, index })}
          />
        ))}
      </div>
    </div>
  );
}

export function StudentBucketRow({
  store,
  bucket,
  index,
}: {
  store: SubmissionReviewStore;
  bucket: { id: string; isNew: boolean; locked: boolean };
  index: number;
}) {
  const { t } = useTranslation(); // <-- NEU
  const { state, actions, lightbox } = store;
  // Either the whole canvas is read-only, or just this (already confirmed) submission is locked.
  const rowLocked = state.isReadOnly || bucket.locked;
  const pages = state.pagesByStudent[bucket.id] || [];
  const name = bucket.isNew
    ? (state.newStudents[bucket.id]?.name ?? t('Canvas.submissionCheck.newStudentIndexed', { index: index + 1 }))
    : (state.editedNames[bucket.id] ?? t('Canvas.submissionCheck.studentIndexed', { index: index + 1 }));
  const splitConfidence = state.splitConfidenceByStudent[bucket.id] ?? 0;
  const splitIssues = state.splitIssuesByStudent[bucket.id] ?? [];

  // Check against German and English placeholders to correctly highlight empty or unedited fields
  const isMissingName =
    name.startsWith('Schüler ') ||
    name.startsWith('Neuer Schüler ') ||
    name.startsWith('Student ') ||
    name.startsWith('New student ') ||
    name.trim() === '';

  const isDropActive = state.dropTarget?.bucketId === bucket.id;

  return (
    <div
      className={`bg-white dark:bg-card border rounded-xl shadow-sm overflow-hidden transition-all duration-200 ${
        isDropActive ? 'ring-2 ring-blue-400 border-transparent shadow-md' : 'border-slate-200'
      }`}
      onDragOver={rowLocked ? undefined : e => actions.handleDragOver(e, bucket.id, -1)}
      onDrop={rowLocked ? undefined : e => actions.handleDrop(e, bucket.id, -1)}
    >
      <div
        className={`@container flex items-center justify-between gap-2 py-1 px-1 border-b border-border ${isMissingName ? 'bg-amber-50/30 dark:bg-amber-900/10' : 'bg-slate-50/50 dark:bg-secondary/30'}`}
      >
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xs font-bold text-slate-400 w-6 text-center shrink-0">{index + 1}.</span>
            <div className="relative min-w-[64px] max-w-[240px] flex-1">
              <input
                value={name}
                disabled={rowLocked}
                onChange={e => {
                  if (bucket.isNew) {
                    actions.updateNewStudentName(bucket.id, e.target.value);
                  } else {
                    actions.updateEditedName(bucket.id, e.target.value);
                  }
                }}
                className={`w-full px-3 py-1.5 text-sm font-medium rounded-md border outline-none transition-colors focus:ring-2 focus:ring-blue-500 ${
                  isMissingName
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-300 placeholder-amber-400 pr-8'
                    : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600 bg-transparent hover:bg-white dark:hover:bg-secondary text-slate-900 dark:text-slate-100'
                }`}
                placeholder={t('Canvas.submissionCheck.enterName')}
              />
              {isMissingName && (
                <AlertTriangle className="w-4 h-4 text-amber-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              )}
            </div>
          </div>
          <span className="inline-flex items-center text-xs font-medium text-slate-500 px-2 py-0.5 bg-slate-200 rounded-full shrink-0">
            <span className="hidden @[22rem]:inline whitespace-nowrap">
              {t('Canvas.submissionCheck.pageX', { count: pages.length })}
            </span>
            <span className="@[22rem]:hidden whitespace-nowrap">{pages.length}</span>
          </span>
          {bucket.locked && (
            <span
              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 select-none shrink-0"
              title={t('Canvas.submissionCheck.lockedHint', {
                defaultValue: 'Bereits bestätigt – kann nicht mehr bearbeitet werden.',
              })}
            >
              <Lock className="w-3 h-3 shrink-0" />
              <span className="hidden @[26rem]:inline whitespace-nowrap">
                {t('Canvas.submissionCheck.locked', { defaultValue: 'Bestätigt' })}
              </span>
            </span>
          )}
          <span
            className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
              splitConfidence >= SPLIT_THRESHOLDS.HIGH_CONFIDENCE
                ? 'bg-emerald-100 text-emerald-700'
                : splitConfidence >= SPLIT_THRESHOLDS.LOW_CONFIDENCE
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
            }`}
            title={t('Canvas.submissionCheck.splitConfidenceHover', { percent: Math.round(splitConfidence * 100) })}
          >
            <span className="hidden @[30rem]:inline whitespace-nowrap">
              {t('Canvas.submissionCheck.splitPercent', { percent: Math.round(splitConfidence * 100) })}
            </span>
            <span className="@[30rem]:hidden whitespace-nowrap">{Math.round(splitConfidence * 100)}%</span>
          </span>
        </div>

        {!rowLocked && (
          <button
            onClick={() => actions.deleteStudent(bucket.id, bucket.isNew)}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition shrink-0"
            title={t('Canvas.submissionCheck.deleteStudent')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="py-2 px-1 bg-white dark:bg-card min-h-[160px] flex overflow-x-auto gap-3 custom-scrollbar">
        {pages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-400 dark:text-slate-500">
            {t('Canvas.submissionCheck.dragHint', { name: name.split(' ')[0] })}
          </div>
        ) : (
          pages.map((page, idx) => (
            <PageThumbnail
              key={page.imageId}
              page={page}
              idx={idx}
              bucketId={bucket.id}
              isReadOnly={rowLocked}
              fallbackUrl={state.fallbackUrls[page.imageId]}
              draggedItem={state.draggedItem}
              dropTarget={state.dropTarget}
              onDragStart={actions.handleDragStart}
              onDragOver={actions.handleDragOver}
              onDrop={actions.handleDrop}
              onPreview={(bucketId, index) => lightbox.actions.setPreviewPage({ bucketId, index })}
            />
          ))
        )}
      </div>

      {splitIssues.length > 0 && (
        <div className="border-t border-amber-100 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-900/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-300">
          <div className="mb-1 font-semibold text-amber-800 dark:text-amber-400">
            {t('Canvas.submissionCheck.assignmentNotes')}
          </div>
          <ul className="space-y-1">
            {splitIssues.map((issue, issueIndex) => (
              <li key={`${bucket.id}-issue-${issueIndex}`} className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function PreviewLightboxModal({ store }: { store: SubmissionReviewStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { lightbox } = store;
  const { state: lbState, actions: lbActions } = lightbox;

  if (!lbState.previewPage || !lbState.activePreviewUrl) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-4"
      onClick={() => lbActions.setPreviewPage(null)}
      role="presentation"
    >
      <div
        className="relative flex max-w-5xl max-h-[85vh] items-center justify-center"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={() => lbActions.setPreviewPage(null)}
          className="absolute -right-3 -top-3 md:-right-5 md:-top-5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/80 text-white hover:bg-black shadow-lg"
        >
          <X className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={lbActions.goToPreviewPrev}
          disabled={!lbState.canPreviewPrev}
          className="absolute left-2 md:-left-14 top-1/2 z-10 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-black/80 text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <button
          type="button"
          onClick={lbActions.goToPreviewNext}
          disabled={!lbState.canPreviewNext}
          className="absolute right-2 md:-right-14 top-1/2 z-10 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-black/80 text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        <img
          src={lbState.activePreviewUrl}
          alt={t('Canvas.submissionCheck.previewPageAlt', { page: (lbState.previewPage?.index ?? 0) + 1 })}
          className="max-h-[85vh] w-auto max-w-full rounded-lg bg-white object-contain shadow-2xl"
        />
      </div>

      <div className="mt-6 shrink-0 rounded-full border border-white/25 bg-black/75 px-5 py-2 text-sm font-semibold text-white whitespace-nowrap shadow-lg tracking-wide">
        {t('Canvas.submissionCheck.pageCurrentOfTotal', {
          current: lbState.previewPage.index + 1,
          total: lbState.previewPages.length,
        })}
      </div>
    </div>,
    document.body,
  );
}
